/* oxlint-disable typescript/no-explicit-any */

import {
  onSnapshot,
  doc,
  getDocs,
  query,
  collection,
  limit,
  where,
  orderBy,
  startAfter,
  DocumentData,
  DocumentReference,
  QuerySnapshot,
  DocumentSnapshot,
  WhereFilterOp,
} from "firebase/firestore";
import { queryBridge } from "../services/logging";
import {
  InfiniteData,
  QueryFunctionContext,
  QueryKey,
  QueryStatus,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

// services
import { semiRandomId } from "../utilities/ids";
import { getTargetFromPath } from "wombat-global/src/constants";
import { subscriptionQueryClient } from "../contexts/react-query/query-client";
import { generateFirestoreConnection } from "../services/firebase.service";
import {
  isOnlineCachedMissingDocumentSnapshot,
  reportMissingIndexedDbDocument,
} from "../services/firebase.indexeddb-recovery";
import { Time } from "wombat-global/src/utilities/time";

export type OrderByType = {
  fieldPath: string;
  directionStr: "desc" | "asc";
};

export type WhereType = {
  fieldPath: string;
  opStr: WhereFilterOp;
  value: unknown;
};

type DocSnapQueryOptions<T> = Omit<
  Partial<UseQueryOptions<T | null, Error, T | null, QueryKey>>,
  "queryKey" | "queryFn"
>;

type DocSnapResult<T> = [
  value: T | undefined,
  state: QueryStatus,
  errors: unknown | undefined | null,
  unSub: (() => void) | undefined,
];

function isSnapshotData<T>(data: unknown): data is T {
  return data !== undefined;
}

/**
 * Add debounce unSub so rapid render unmount doesn't spam firebase sdk
 * - with out this it sub value updates didn't come through after being unsubscribed
 *   (training matrix update after navigating to and from that page didn't come through)
 *
 * - Garbage Collection (GC) timing of this cache must be larger than any of the debounce time
 *   If it not then the __unsubscribe() cant be called before that object is cleaned up
 */
const unsubscribeDebounceMap: Record<string, any> = {};

subscriptionQueryClient.getQueryCache().subscribe(({ type, query: q }) => {
  if (!(q.options as any).enabled) {
    return;
  }

  if (
    type === "observerRemoved" ||
    type === "updated" ||
    type === "added" ||
    type === "removed" ||
    type === "observerAdded"
  ) {
    queryBridge.chirp("    QUERY BRIDGE EVENT:", type, q);
  }

  if (
    type === "observerRemoved" &&
    q?.getObserversCount() === 0 &&
    (q as any).__unsubscribe
  ) {
    //
    if (unsubscribeDebounceMap[q.queryKey]) {
      queryBridge.debug("unSub debounced", q.queryKey);
      clearTimeout(unsubscribeDebounceMap[q.queryKey]);
    }

    unsubscribeDebounceMap[q.queryKey] = setTimeout(() => {
      const _q = subscriptionQueryClient
        .getQueryCache()
        .find({ queryKey: q.queryKey, exact: true });

      if (_q && (_q as any).__unsubscribe && _q.getObserversCount() === 0) {
        queryBridge.debug("removing FB sub", _q);
        (_q as any).__unsubscribe();
        // Remove cache item so that following subscribes will initiate correctly
        subscriptionQueryClient.getQueryCache().remove(_q);
      }
    }, Time.seconds * 1);
  }
});

function createQuery<T = DocumentData>(
  getQuery: () => Promise<DocumentReference<DocumentData, DocumentData>>, // | Query<T, B>,
): (context: QueryFunctionContext) => Promise<T | null> {
  return async (context: QueryFunctionContext): Promise<T | null> => {
    let firstRun = true;
    let unsubscribe: (() => void) | undefined;

    const _getRef = await getQuery();

    const data = await new Promise<T | null>((resolve, reject) => {
      queryBridge.log("call snapshot", _getRef.path);
      unsubscribe = onSnapshot(
        _getRef,
        { includeMetadataChanges: true },
        {
          next: (value) => {
            const tr = "(" + semiRandomId() + ")";
            const cacheHit = value.metadata.fromCache ? "[fromCache]" : "";
            const snapshotData = value.data();
            void reportMissingIndexedDbDocument({
              ref: _getRef,
              snapshot: value,
            });

            if (isOnlineCachedMissingDocumentSnapshot(value)) {
              queryBridge.warn(
                tr,
                "doc cache miss; awaiting server",
                cacheHit,
                _getRef.path,
              );
              return;
            }

            if (firstRun) {
              queryBridge.log(tr, "first run done", cacheHit, _getRef.path);
              resolve(isSnapshotData<T>(snapshotData) ? snapshotData : null);
              firstRun = false;
            }

            if (!value.exists()) {
              queryBridge.warn(tr, "doc dne", cacheHit, _getRef.path);
              subscriptionQueryClient.setQueriesData(
                { queryKey: context.queryKey, exact: true },
                () => null,
              );
            } else {
              queryBridge.log(
                tr,
                "doc snap",
                cacheHit,
                snapshotData,
                _getRef.path,
              );
              subscriptionQueryClient.setQueriesData(
                { queryKey: context.queryKey, exact: true },
                () => (isSnapshotData<T>(snapshotData) ? snapshotData : null),
              );
            }
          },
          error: (error) => {
            queryBridge.error(error);
            reject(error);
          },
          complete: () => {
            queryBridge.warn("Completed callback unused");
          },
        },
      );

      const q = subscriptionQueryClient.getQueryCache().find({
        queryKey: context.queryKey,
        exact: true,
      });

      if (q && !("__unsubscribe" in q)) {
        Object.assign(q, { __unsubscribe: unsubscribe });
      }
    });

    // Get query object from React Query
    const q = subscriptionQueryClient.getQueryCache().find({
      queryKey: context.queryKey,
      exact: true,
    });

    queryBridge.log("end snapshot", _getRef.path, q);

    // MUTATION! Remove existing subscription (if there is one) and store new one on `query` object (cleanup)
    if (q && !("__unsubscribe" in q)) {
      Object.assign(q, { __unsubscribe: unsubscribe });
    }

    return data;
  };
}

export type CollectionQueryProps = {
  path: string;
  orderBy?: OrderByType[];
  where?: WhereType[];
  limit?: number;
};
/**
 * getCollection data
 * @param colPath string
 * @param options
 * @returns
 */
export function useCol<T = unknown>(
  args: CollectionQueryProps,
  options: Omit<
    Partial<
      UseInfiniteQueryOptions<
        QuerySnapshot,
        Error,
        InfiniteData<QuerySnapshot<T>>,
        QueryKey,
        DocumentSnapshot | undefined
      >
    >,
    "queryKey" | "queryFn" | "getNextPageParams"
  > = {},
) {
  const res = useInfiniteQuery<
    QuerySnapshot,
    Error,
    InfiniteData<QuerySnapshot<T>>,
    QueryKey,
    DocumentSnapshot | undefined
  >(
    {
      ...options,
      initialPageParam: options.initialPageParam || undefined,
      queryKey: [
        "fb-col-query",
        args.path,
        JSON.stringify(args.orderBy),
        JSON.stringify(args.where),
        args.limit,
      ],
      queryFn: async ({ pageParam = undefined, ...rest }) => {
        const tr = "(" + semiRandomId() + ")";
        const fb = await generateFirestoreConnection(
          getTargetFromPath(args.path),
        );

        queryBridge.debug(tr, "col", { ...rest, pageParam }, args);
        let req = query(collection(fb, args.path));

        // each search should be saved in browser memory, so cached
        // thats so navigation to and from a page will not re-query
        // const pathHash = this.encodeKey(key, orderBy, where, startAfter?.id);
        req = query(req, limit(args?.limit || 25));

        if (
          args.where?.length &&
          args.where.some((whr) => operatorsNeedOrderBy.includes(whr.opStr))
        ) {
          const _orderBy = args.where
            .filter(
              (whr) =>
                !args.orderBy?.some((order) => order.fieldPath === whr.fieldPath),
            )
            .map((whr) => orderBy(whr.fieldPath));
          queryBridge.debug(tr, "col", "needs orderby", _orderBy);
          // handle one of the firebase limitations
          // "If you include a filter with a range comparison (<, <=, >, >=, not-in),
          // your first ordering must be on the same field"
          // https://firebase.google.com/docs/firestore/query-data/order-limit-data#limitations
          req = query(req, ..._orderBy);
        }

        if (args.orderBy?.length) {
          const _orderBy = args.orderBy.map((order) =>
            orderBy(order.fieldPath, order.directionStr),
          );
          queryBridge.debug(tr, "col", "orderby", _orderBy);
          req = query(req, ..._orderBy);
        }

        if (args.where?.length) {
          const _where = args.where.map((whr) =>
            where(whr.fieldPath, whr.opStr, whr.value),
          );
          queryBridge.debug(tr, "col", "where", _where);
          req = query(req, ..._where);
        }

        if (pageParam) {
          req = query(req, startAfter(pageParam));
        }

        return getDocs(req).then((d) => {
          if (d.metadata.fromCache) {
            queryBridge.debug(tr, "col [fb cache-hit]", "completed", d);
          } else {
            queryBridge.debug(tr, "col", "completed", d);
          }
          return d;
        });
      },
      getNextPageParam: (lastPage) => {
        return lastPage.empty ||
          lastPage.size < (args.limit || 25) ||
          lastPage.docs === undefined
          ? undefined
          : lastPage.docs[lastPage.docs.length - 1];
      },
    },
    subscriptionQueryClient,
  );

  if (res.error) {
    queryBridge.error(
      "col",
      args.path,
      args,
      res.error,
      res.errorUpdateCount,
      res.errorUpdatedAt,
    );
  }

  return res;
}

/**
 * Subscribe to document listeners
 */
export function useDocSnap<T>(
  docPath: string,
  options: DocSnapQueryOptions<T> = {},
): DocSnapResult<T> {
  /**
   * Options 2
   * - does not add a lot of chatter though
   * - I don't really understand this one very well.
   */

  const res = useQuery<T | null>(
    {
      ...options,
      queryKey: ["fb-doc-subscription", docPath],
      queryFn: createQuery<T>(() =>
        generateFirestoreConnection(getTargetFromPath(docPath)).then((fb) =>
          doc(fb, docPath),
        ),
      ),
    },
    subscriptionQueryClient,
  );
  const data = res.data === null ? undefined : res.data;

  return [data, res.status, res.error, () => {}];
}

export const operatorsNeedOrderBy = ["<", "<=", ">", ">=", "!=", "not-in"];

function mergePages<T = unknown>(acc: T[], page?: QuerySnapshot<T>) {
  if (page?.docs) {
    acc.push(...page.docs.map((d) => d.data()));
  }
  return acc;
}

export function mergeInfiniteQueryData<T>(
  res?: InfiniteData<QuerySnapshot<T>>,
) {
  return res?.pages.reduce(mergePages, [] as T[]) || [];
}

/**
 * Subscribe to multiple document listeners
 */
export function useDocSnapMulti<T>(
  docPaths: string[],
  options?: Omit<UseQueryOptions, "query" | "queryKey">,
): [
  value: T | undefined,
  state: QueryStatus,
  errors: unknown | undefined | null,
  unSub: (() => void) | undefined,
][] {
  const res = useQueries(
    {
      queries: docPaths.map((p) => ({
        queryKey: ["fb-doc-subscription", p],
        queryFn: createQuery(() =>
          generateFirestoreConnection(getTargetFromPath(p)).then((fb) =>
            doc(fb, p),
          ),
        ),
        ...options,
      })),
    },
    subscriptionQueryClient,
  );

  return res.map((r) => [r.data as T, r.status, r.error, () => {}]);
}

/**
 * Subscribe to multiple document listeners
 */
export function useNewDocSnapMulti<T>(
  docPaths: string[],
  options?: Omit<UseQueryOptions, "query" | "queryKey">,
): UseQueryResult<T>[] {
  const res = useQueries(
    {
      queries: docPaths.map((p) => ({
        queryKey: ["fb-doc-subscription", p],
        queryFn: createQuery(() =>
          generateFirestoreConnection(getTargetFromPath(p)).then((fb) =>
            doc(fb, p),
          ),
        ),
        ...options,
      })),
    },
    subscriptionQueryClient,
  );

  return res as UseQueryResult<T>[];
}
